import { useCallback } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import {
    interpolate,
    runOnJS,
    useAnimatedProps,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { cartesianToPolar, getRadianByValue, polarToCartesian } from '../utils';
import { useRadialSlider } from './use-radial-slider';

export type RadialSliderAnimationHookProps = {
    radius: number;
    sliderWidth: number;
    thumbRadius: number;
    openingRadian: number;
    min: number;
    max: number;
    minAllowable: number;
    onChange?: (v: number) => void;
    value: number;
    sliderPadding: number;
    sliderBorderWidth: number;
};

export const useSliderAnimation = (props: RadialSliderAnimationHookProps) => {
    const {
        radius,
        sliderWidth,
        thumbRadius,
        openingRadian,
        min,
        max,
        onChange,
        minAllowable,
        value,
        sliderPadding,
        sliderBorderWidth,
    } = props;

    // Validate props
    if (min >= max) {
        throw new Error('min must be less than max');
    }
    if (minAllowable < 0 || minAllowable > max - min) {
        throw new Error('minAllowable must be between 0 and max-min');
    }

    // Keep track of the current value of the slider
    const currentStep = useSharedValue(value);

    // Get initial values and calculated layout params from useRadialSlider
    const { radianValue, startRadian, startPoint, centerOffset } = useRadialSlider({
        radius,
        sliderWidth,
        openingRadian,
        thumbRadius,
        max,
        sliderPadding,
        sliderBorderWidth,
    });

    // Keep track of the start radian and cartesian coordinates of the thumb
    const moveStartRadian = useSharedValue(getRadianByValue(currentStep.value, openingRadian, max, min));
    const moveStartCartesian = useSharedValue(polarToCartesian(moveStartRadian.value, radius, centerOffset));

    // Keep track of the current x,y position of the thumb
    const thumbPosition = useSharedValue({ x: moveStartCartesian.value.x, y: moveStartCartesian.value.y });

    // Keep track of the current radian of the slider track
    const currentRadian = useSharedValue(moveStartRadian.value);

    // Initialize the max and min radians of the slider track
    const maxRadian = useSharedValue(getRadianByValue(min + minAllowable, openingRadian, max, min));
    const minRadian = useSharedValue(getRadianByValue(max, openingRadian, max, min));

    const startStep = useSharedValue(currentStep.value);

    // Animated props for the slider track
    const animatedSliderProps = useAnimatedProps(() => ({
        d: `M${startPoint.x},${startPoint.y} A ${radius},${radius},0,${
            startRadian - currentRadian.value >= Math.PI ? '1' : '0'
        },1,${thumbPosition.value.x},${thumbPosition.value.y}`,
    }));

    const animatedThumbStyle = useAnimatedStyle(() => {
        const translateX = thumbPosition.value.x - thumbRadius;
        const translateY = thumbPosition.value.y - thumbRadius;

        return {
            position: 'absolute', // Ensure positioning context
            transform: [{ translateX }, { translateY }],
        };
    });

    // Add animation progress tracking
    const animationProgress = useSharedValue(0);
    const animationStartRadian = useSharedValue(0);
    const animationTargetRadian = useSharedValue(0);

    useAnimatedReaction(
        () => currentStep.value,
        (result, previous) => {
            if (result !== previous && onChange && previous !== null) {
                runOnJS(onChange)(result);
            }
        },
        []
    );

    const updateThumbPosition = useCallback(
        (thumbValue: number) => {
            'worklet';
            const radian = getRadianByValue(thumbValue, openingRadian, max, min);
            const { x: newX, y: newY } = polarToCartesian(radian, radius, centerOffset);
            thumbPosition.value = { x: newX, y: newY };
            currentStep.value = thumbValue;
            currentRadian.value = radian;
        },
        [currentStep, currentRadian, openingRadian, max, min, radius, centerOffset, thumbPosition]
    );

    const handlePanGesture = Gesture.Pan()
        .onBegin(() => {
            'worklet';
            startStep.value = currentStep.value;
            moveStartRadian.value = currentRadian.value;
            moveStartCartesian.value = { x: thumbPosition.value.x, y: thumbPosition.value.y };
        })
        .onUpdate(event => {
            'worklet';
            let { x, y } = moveStartCartesian.value;
            x += event.translationX;
            y += event.translationY;

            const currentRadianValue = cartesianToPolar(x, y, centerOffset);
            if (currentRadianValue > maxRadian.value || currentRadianValue < minRadian.value) {
                return;
            }

            // get the current radian based on the user's pan gesture
            currentRadian.value = currentRadianValue;

            // get the new cartesian coordinates based on the current radian
            const { x: newX, y: newY } = polarToCartesian(currentRadian.value, radius, centerOffset);

            // update the thumb position
            thumbPosition.value = { x: newX, y: newY };

            // find the next step based on the current radian
            const ratio = (moveStartRadian.value - currentRadian.value) / ((Math.PI - radianValue) * 2);
            let nextStep = startStep.value + Math.round(ratio * (max - min));

            // Calculate the radian distance to the nearest step
            const stepRadian = getRadianByValue(nextStep, openingRadian, max, min);
            const radianDistance = Math.abs(currentRadian.value - stepRadian);

            // Calculate threshold based on thumb radius (convert radius to radians)
            const radianThreshold = Math.atan2(thumbRadius, radius);

            if (radianDistance < radianThreshold) {
                currentStep.value = Math.max(min, Math.min(max, nextStep));
            }
        })
        .onEnd(() => {
            'worklet';
            // Store the start and target radians for animation
            animationStartRadian.value = currentRadian.value;
            animationTargetRadian.value = getRadianByValue(currentStep.value, openingRadian, max, min);

            // Start the animation
            animationProgress.value = 0;
            animationProgress.value = withTiming(1, { duration: 100 }, () => {
                'worklet';
                // Update final position after animation
                currentRadian.value = animationTargetRadian.value;
                thumbPosition.value = polarToCartesian(currentRadian.value, radius, centerOffset);
            });
        });

    // Add animated reaction to update position during animation
    useAnimatedReaction(
        () => animationProgress.value,
        progress => {
            if (progress > 0 && progress < 1) {
                // Interpolate between start and target radians
                const interpolatedRadian = interpolate(
                    progress,
                    [0, 1],
                    [animationStartRadian.value, animationTargetRadian.value]
                );

                // Update both the radian and position to keep them in sync
                currentRadian.value = interpolatedRadian;
                const { x, y } = polarToCartesian(interpolatedRadian, radius, centerOffset);
                thumbPosition.value = { x, y };
            }
        }
    );

    return {
        updateThumbPosition,
        animatedSliderProps,
        animatedThumbStyle,
        handlePanGesture,
    };
};
